import { useInvitePreview, useRedeemInvite } from '@hobbytools/data';
import { appUrl, friendlyError, systemAppId, useAuth } from '@hobbytools/platform';
import { Banner, Button } from '@hobbytools/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AuthLayout, linkClass } from './AuthLayout';

const invalidReason: Record<string, string> = {
  revoked: 'This invite was withdrawn by the group.',
  expired: 'This invite has expired.',
  used_up: 'This invite has been used the maximum number of times.',
  archived: 'This group has been archived.',
  closed: "This group isn't accepting invite codes right now.",
  campaign_archived: 'This campaign has been archived.',
  campaign_closed: "This campaign isn't taking new players right now.",
};

/** /join/:code — shows what the invite leads to (works signed out), then joins after sign-in. */
export function Join() {
  const { code = '' } = useParams();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const preview = useInvitePreview(code);
  const redeem = useRedeemInvite();
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<string | null>(null);
  const next = `?next=${encodeURIComponent(`/join/${code}`)}`;

  if (preview.isLoading || isLoading) {
    return <AuthLayout title="Checking your invite…">{null}</AuthLayout>;
  }

  if (!preview.data) {
    return (
      <AuthLayout title="Invite not found">
        <Banner tone="warning">
          We couldn't find an invite with the code {code.toUpperCase()}. Check it with whoever sent it.
        </Banner>
        <Link className={linkClass} to="/">
          Back to home
        </Link>
      </AuthLayout>
    );
  }

  const { groupName, memberCount, message, isValid, reason, campaignId, campaignName, campaignSystem } =
    preview.data;
  const campaignApp = campaignSystem ? systemAppId(campaignSystem) : undefined;
  const players = `${memberCount} ${memberCount === 1 ? 'player' : 'players'} so far.`;

  const join = async () => {
    setError(null);
    try {
      const result = await redeem.mutateAsync(code);
      if (!result.campaignId) {
        navigate(`/groups/${result.groupId}`, { replace: true });
      } else if (result.status !== 'approved') {
        setWaiting('Your request is waiting for a group admin.');
      } else if (campaignApp) {
        // The campaign lives in its system's app: a full page load (ADR 0008). A pending request shows there too.
        window.location.assign(appUrl(campaignApp, `/campaigns/${result.campaignId}`));
      } else {
        navigate(`/groups/${result.groupId}`, { replace: true });
      }
    } catch (err) {
      setError(friendlyError(err, 'invite'));
    }
  };

  return (
    <AuthLayout
      title={campaignId && campaignName ? `Join ${campaignName}` : `Join ${groupName}`}
      lead={campaignId ? `A campaign in ${groupName}. ${players}` : players}
    >
      {message ? <p className="rounded-md bg-sunken p-3 text-sm">“{message}”</p> : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {waiting ? <Banner>{waiting}</Banner> : null}
      {!isValid ? (
        <Banner tone="warning">
          {invalidReason[reason ?? ''] ?? 'This invite is no longer valid.'} Ask for a new one.
        </Banner>
      ) : waiting ? null : user ? (
        <Button onPress={join} isDisabled={redeem.isPending}>
          {redeem.isPending ? 'Joining…' : campaignId ? 'Join the campaign' : `Join ${groupName}`}
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-fg-muted">Sign in or create an account to join.</p>
          <Link to={`/sign-up${next}`} className="contents">
            <Button>Create an account</Button>
          </Link>
          <Link to={`/sign-in${next}`} className="contents">
            <Button variant="secondary">I already have an account</Button>
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
